import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, Skeleton } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel } from '../../components/ui';
import OrdersTimeTable from '../../components/OrdersTimeTable';
import OrdersTimeGraph from '../../components/OrdersTimeGraph';
import OrdersProductionPopUp from '../../components/OrdersProductionTime';
import { OrdersTime } from '../../store/slices';
import { formatDate } from '../../lib/formatDate';
import { useStyles } from './styles';

const DEFAULT_PAGING_STATE = {
    perPage: 25,
    page: 1,
    total: 0,
};

/** `/track-order`: the standard and expedited production times shown to customers, with their change history. */
const TrackPage = () => {
    const { ordersTime, status, queryParams } = useSelector<any, { ordersTime: any; status: string; queryParams: any }>((state) => ({
        ordersTime: state.ordersTime.data,
        status: state.ordersTime.status,
        queryParams: state.ordersTime.queryParams,
    }));
    const [isPopUp, setPopUp] = useState(false);
    const dispatch = useDispatch();
    const classes = useStyles();

    const popUpHandler = () => setPopUp((prev) => !prev);
    const loadOrdersTime = (params: any) => {
        dispatch<any>((OrdersTime.getOrdersTime as any)(params));
    };

    useEffect(() => {
        loadOrdersTime({
            page: queryParams?.page || DEFAULT_PAGING_STATE.page,
            perPage: queryParams?.perPage || DEFAULT_PAGING_STATE.perPage,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- first load only
    }, []);

    const current = ordersTime?.currentProductionTime;
    const rows: any[] = Array.isArray(ordersTime?.result) ? ordersTime.result : [];
    const loading = status === 'loading';
    const failed = status === 'rejected';
    const lastChange = rows[0]?.timestamp ? formatDate(rows[0].timestamp, { time: true }) : null;

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Production"
                title="Production time"
                subtitle="The standard and expedited production times customers see at checkout, and every change made to them."
                actions={(
                    <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={popUpHandler}>
                        Update production time
                    </Button>
                )}
            />
            <div className={classes.kpis}>
                <Panel padding="sm">
                    <div className={classes.kpiLabel}>Standard production time</div>
                    <div className={classNames(classes.kpiValue, classes.standard)}>
                        {current?.standardProductionTime ?? '—'}
                        {lastChange && <span className={classes.kpiHint}>since {lastChange}</span>}
                    </div>
                </Panel>
                <Panel padding="sm">
                    <div className={classes.kpiLabel}>Expedited production time</div>
                    <div className={classNames(classes.kpiValue, classes.expedited)}>{current?.expeditedProductionTime ?? '—'}</div>
                </Panel>
            </div>
            <div className={classes.stack}>
                {loading && <Skeleton animation="wave" width="100%" height={420} variant="rounded" />}
                {!loading && failed && (
                    <Panel>
                        <EmptyState title="The production time could not be loaded" hint="Check the connection and try again." action={<Button variant="outlined" onClick={() => loadOrdersTime(queryParams || DEFAULT_PAGING_STATE)}>Retry</Button>} />
                    </Panel>
                )}
                {!loading && !failed && rows.length === 0 && (
                    <Panel>
                        <EmptyState title="No changes recorded yet" hint="Updates made with the button above appear here." />
                    </Panel>
                )}
                {!loading && !failed && rows.length > 0 && (
                    <>
                        <Panel title="Last 12 changes" padding="sm">
                            <div className={classes.chart}>
                                <OrdersTimeGraph data={rows.slice(0, 12)} />
                            </div>
                        </Panel>
                        <Panel title="History" padding="none">
                            <OrdersTimeTable ordersTime={rows} />
                        </Panel>
                    </>
                )}
            </div>
            <OrdersProductionPopUp
                isPopUp={isPopUp}
                popUpHandler={popUpHandler}
                loadOrders={loadOrdersTime}
                queryParams={queryParams || DEFAULT_PAGING_STATE}
                initialValues={current}
            />
        </PageContainer>
    );
};

export default TrackPage;
